import React from "react";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import {
  Card,
  ScrollArea,
  TrendIndicator,
  type TrendIndicatorProps,
} from "@outpave/ds";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatCash } from "utils/formatCash";
import CustomSkeleton from "components/common/CustomSkeleton";
import { Center, Inline } from "components/layout/reusableLayouts";
import type { AccountsLocationState } from "pages/platform/Accounts";
import type { PlatformCompany } from "./types";

const TOP_COMPANIES = 5;

interface TopCompaniesPanelProps {
  companies: PlatformCompany[];
  loading?: boolean;
}

/**
 * A null change means the company has no prior period, which is the absence of
 * a trend rather than a flat one, so it gets a label and no glyph.
 */
const trendFor = (
  changePercent: number | null,
  newLabel: string,
): TrendIndicatorProps => {
  if (changePercent === null) return { text: newLabel };
  const text = `${Math.abs(changePercent).toFixed(1)}%`;
  if (changePercent > 0) return { direction: "up", text };
  if (changePercent < 0) return { direction: "down", text };
  return { direction: "flat", text };
};

/**
 * The companies spending the most. The trend and the amount sit in columns of
 * their own, so they line up down the card whatever the length of each name. A
 * row opens Accounts already searched to its company.
 */
const TopCompaniesPanel: React.FC<TopCompaniesPanelProps> = ({
  companies,
  loading = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card
      variant="outlined"
      title={t("Top accounts")}
      subtitle={t("Largest programs by volume")}
      sx={{ height: "100%" }}
    >
      {loading && companies.length === 0 ? (
        <CustomSkeleton variant="list" rows={4} />
      ) : companies.length === 0 ? (
        <Center sx={{ flex: 1, justifyContent: "center", textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{ color: "var(--color-text-tertiary)" }}
          >
            {t("No Data Found")}
          </Typography>
        </Center>
      ) : (
        <ScrollArea sx={{ flex: 1, minHeight: 0 }}>
          <List disablePadding>
            {companies.slice(0, TOP_COMPANIES).map((company) => (
              <ListItemButton
                key={company.id}
                onClick={() => {
                  const state: AccountsLocationState = {
                    search: company.companyName,
                  };
                  navigate("/platform/accounts", { state });
                }}
              >
                <Typography
                  variant="subtitle2"
                  noWrap
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  {company.companyName}
                </Typography>
                <Inline
                  gap="var(--space-3)"
                  wrap={false}
                  sx={{ flexShrink: 0 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      minWidth: 72,
                    }}
                  >
                    <TrendIndicator
                      {...trendFor(company.changePercent, t("New"))}
                    />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      minWidth: 64,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatCash(company.totalSpend)}
                  </Typography>
                </Inline>
              </ListItemButton>
            ))}
          </List>
        </ScrollArea>
      )}
    </Card>
  );
};

export default TopCompaniesPanel;
